import ms from 'ms';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type { UseShouldDisplayInternalMessagingActionProps } from 'hb-react/frontoffice/utils/messaging';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

const useShouldDisplayInternalMessagingAction = ({
  user,
  onlyCurrentUser = false,
  // Remove this property when removing flagUjbPeopleDirectoryReworkPerformances
  isFeatureActive = true,
}: UseShouldDisplayInternalMessagingActionProps) => {
  const { currentUser } = useDefaultAppContexts();
  const {
    // @ts-expect-error: legacy flag, untyped.
    messaging: { stanfordRequirements: stanfordMessagingRequirements },
  } = useFeatureFlag();

  const { data: currentUserCanMessageAnotherUser } = useQuery(
    ['userIsValid', user?.id],
    () => isUserIsValidRequest(user),
    {
      enabled:
        !stanfordMessagingRequirements &&
        !onlyCurrentUser &&
        !!user &&
        !!currentUser &&
        currentUser.id !== user.id,
      staleTime: ms('5m'),
    },
  );

  const { data: currentUserHasAccessRightToInternalMessaging } = useQuery(
    'currentUserHasAccessRightToInternalMessaging',
    currentUserHasAccessRightToInternalMessagingRequest,
    {
      enabled:
        onlyCurrentUser && !!currentUser && stanfordMessagingRequirements,
      staleTime: ms('5m'),
    },
  );

  const { data: currentUserAndTargetUserHaveAccessRightToInternalMessaging } =
    useQuery(
      ['currentUserAndTargetUserHaveAccessRightToInternalMessaging', user?.id],
      () =>
        currentUserAndTargetUserHaveAccessRightToInternalMessagingRequest(
          currentUser,
          user,
        ),
      {
        enabled:
          !onlyCurrentUser &&
          !!currentUser &&
          !!user &&
          stanfordMessagingRequirements,
        staleTime: ms('5m'),
      },
    );

  return useMemo(() => {
    if (!currentUser || !user || !isFeatureActive) {
      return false;
    }

    const currentUserCanAccessInternalMessaging =
      currentUser.can.access_internal_message;

    if (
      currentUserCanAccessInternalMessaging &&
      stanfordMessagingRequirements
    ) {
      return onlyCurrentUser
        ? currentUserHasAccessRightToInternalMessaging
        : currentUserAndTargetUserHaveAccessRightToInternalMessaging;
    }

    return onlyCurrentUser
      ? currentUserCanAccessInternalMessaging
      : currentUserCanAccessInternalMessaging &&
          !!currentUserCanMessageAnotherUser;
  }, [
    currentUser,
    currentUserAndTargetUserHaveAccessRightToInternalMessaging,
    currentUserCanMessageAnotherUser,
    currentUserHasAccessRightToInternalMessaging,
    isFeatureActive,
    onlyCurrentUser,
    stanfordMessagingRequirements,
    user,
  ]);
};

const isUserIsValidRequest = (user) => {
  // @ts-expect-error: Hivebrite is not typed, i'll adjust this later.
  const accessToken = window.Hivebrite.data.access_token;

  return axios
    .post('/api/user/v1/messaging/validate', {
      access_token: accessToken,
      user_id: user.id,
    })
    .then((response) => response.data.user.can);
};

const currentUserHasAccessRightToInternalMessagingRequest = async () => {
  // @ts-expect-error: Hivebrite is not typed, i'll adjust this later.
  const accessToken = window.Hivebrite.data.access_token;

  const result = await axios
    .post('/api/user/v1/messaging/access', {
      access_token: accessToken,
    })
    .then(() => true)
    .catch(() => false);

  return result;
};

const currentUserAndTargetUserHaveAccessRightToInternalMessagingRequest =
  async (currentUser, user) => {
    if (currentUser.id === user.id) {
      return false;
    }

    // @ts-expect-error: Hivebrite is not typed, i'll adjust this later.
    const accessToken = window.Hivebrite.data.access_token;

    const currentUserCanMessageUser = await axios
      .post('/api/user/v1/messaging/access', {
        access_token: accessToken,
        scope: 'users',
        user_ids: [user.id],
      })
      .then((response) => response.data.user_ids[0].can);

    return currentUserCanMessageUser;
  };

export default useShouldDisplayInternalMessagingAction;
